import React, { useState, Fragment, useEffect } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'

import { Form, Checkbox, Select } from 'components/forms'
import { ContentBlock } from 'components/ContentBlock'
import TableSets from './fragments/TableSets'
import SetDetails from './fragments/SetDetails'
import { Mutation } from 'components/graphql'
import { updateProduct } from '../../gql/mutation'
import { getProduct_Sets } from '../../gql/query/getProduct'
import fetchedDataToForm from 'helpers/fetchedDataToForm'
import { loadAppConstants } from 'gql/query'
import { withTranslation } from 'react-i18next'
import getQueryName from 'helpers/getNameOfGqlQuery'
import { changeLabel } from 'sections/utils'
import { useQuery } from '@apollo/client'

const Selection = ({ data = {}, formData = {}, mutations: { updateItem }, itemId }) => {
	const [selectedSet, setSelectedSet] = useState(null)
	const [t, i18n] = useTranslation()
	const { data: dataPriceDifferent } = useQuery(loadAppConstants, { variables: { type: 61, language: i18n.language }, fetchPolicy: 'cache-first' })
	const optionsPriceDifference = changeLabel(dataPriceDifferent?.cashAssist?.loadAppConstants, 'product_section.fields.price_difference_option', t)

	const setDefaultValues = {
		minQuantity: 1,
		maxQuantity: 1,
		priceLevel: 99,
		isCollectPoints: false,
		fixProducts: []
	}

	const handleSetChange = ({ setInd, ...vals }, sets, mutate) => {
		const formatVals = objVal => Object.keys(objVal).reduce((acc, field) => {
			switch(field) {
				case 'fixProducts':
					const formattedProds = objVal[field].map(({ code, amount, price }) => ({ code, amount, price }))
					return { ...acc, [field]: formattedProds }
				case 'category': return { ...acc, [field]: objVal[field].id }
				case 'setInd': return acc
				default: return { ...acc, [field]: objVal[field] }
			}
		}, {})
		// If no other fields except 'setInd' was provided that means set was deleted
		const isSetDeleted = !Object.keys(vals).length
		let updatedSetInd = null
		const newSets = isSetDeleted ?
			sets.reduce((acc, { setInd: itemInd, ...restSetVals }) => Number(itemInd) === Number(setInd) ? acc : [...acc, restSetVals], [])
			:
			sets.map(({ setInd: itemInd, ...restSetVals }, i) => {
				if(itemInd === setInd) {
					updatedSetInd = i
					return vals
				}
				return restSetVals
			})
		if(updatedSetInd === null && !isSetDeleted) {
			newSets.push(vals)
		}
		const mutationName = getQueryName(updateProduct.sets)
		const optimisticSets = newSets.map(item => {
			const fullDataSet = sets.find(({ category: { id } }) => Number(item.category.id) === Number(id))
			// If category was not fined in all sets, get category from updated values because it was updated
			const { gridProducts, ...optimisticCategory } = fullDataSet ? fullDataSet.category : vals.category
			const fixProducts = item.fixProducts.map(({ id, ...restVals }) => ({ ...restVals, __typename: 'SetFixedProduct' }))
			return { ...item, category: { ...optimisticCategory, __typename: 'Category' }, fixProducts, __typename: 'Set' }
		})
		const currentInd = selectedSet
		// Update selected set index only if set is selected (currentInd !== null) and selected set isn't new creating one (currentInd !== -1)
		if(isSetDeleted && (currentInd !== null && currentInd !== -1)) {
			let newInd
			// Deselect set if deleting set is currently selected
			if(currentInd === setInd) {
				newInd = null
			}
			// Decrease setInd to stay opened current set if deleting previous set
			if(currentInd > setInd) {
				newInd = Number(currentInd - 1)
			}
			setSelectedSet(newInd)
		}
		const mutationFields = newSets.map(formatVals)
		mutate({
			variables: {
				id: data.id,
				fields: mutationFields
			},
			optimisticResponse: {
				cashAssist: {
					[mutationName]: {
						__typename: 'Product',
						id: data.id,
						mod_ver: -1,
						sets: optimisticSets
					},
					__typename: 'CA_Mutation'
				}
			},
			...!updatedSetInd && {
				refetchQueries: [{
					query: getProduct_Sets,
					variables: { id: itemId }
				}]
			}
		})
	}

	const existingCheck = (val, allVals) => allVals.some(({ id }) => Number(id) === Number(val))

	const { sets: setsArr = [], ...restData } = formData
	const formattedSets = setsArr.map((item, i) => {
		const { fixProducts, category = {} } = item
		const { gridProducts = [] } = category ? category : {}
		const formattedFixProds = fixProducts.reduce((acc, fixProd) => {
			const productData = gridProducts.find(({ product }) => product ? product.code === fixProd.code : false)
			if(!productData) {
				return acc
			}
			const { productId: id, product: { name } } = productData
			acc.push({ ...fixProd, id, name })
			return acc
		}, [])
		const filteredData = fetchedDataToForm({ ...item, fixProducts: formattedFixProds }, ['gridProducts'])
		return { ...filteredData, setInd: i }
	})
	const getSetData = () => {
		return selectedSet >= 0 ? formattedSets.find(item => item.setInd === selectedSet) || null : setDefaultValues
	}

	return(
		<Fragment>
			<ContentBlock>
				<Form initialValues={restData} onSubmit={updateItem} smartSubmit>
					{({ formState: { values } }) => (
						<Row>
							<Col xs>
								<Select
									field='priceDiffMode'
									label={t('product_section.fields.price_difference_mode')}
									options={optionsPriceDifference} />
								<div {...(Number(values.setsMode) === 1) && { style: { visibility: 'hidden' } }}>
									<Checkbox field='fixProductsAutoSelect' label={t('product_section.fields.autoselect_fix_products')} />
									<Checkbox field='isPricesNormalByDefaultInSet' label={t('product_section.fields.autoactivate_in_cafe')} />
								</div>
							</Col>
							<Col xs>
								<Select label={t('product_section.strings.type')} field='setsMode'
									options={[
										{ value: 0, label: t('product_section.strings.standard') },
										{ value: 1, label: t('product_section.strings.construction_mode') }
									]}
								/>
								<Checkbox field='isMenuName' label={t('product_section.fields.isMenuName')} />
								<Checkbox field='properties.isMenuQuickAdd' label={t('product_section.strings.isMenuQuickAdd')} />
							</Col>
							{/* <Col xs>
								<Select label={t('product_section.fields.printing_time')} field='printChosenSubProductsFlag'
									options={[
										{ value: 0, label: t('product_section.strings.during_payment') },
										{ value: 1, label: t('product_section.strings.after_selection_close') }
									]} />
								<Checkbox field='isFastSelection' label={t('product_section.fields.quick_selection')} />
								<Checkbox field='isForTasting' label={t('product_section.fields.list_as_tasting')} />
								<Checkbox field='printChosenSubProducts' label={t('product_section.fields.print_products_on_label')} />
							</Col> */}
						</Row>
					)}
				</Form>
			</ContentBlock>
			<Mutation mutation={updateProduct.sets}>
				{mutate => (
					<Fragment>
						<ContentBlock>
							<TableSets
								data={formattedSets}
								onSelect={setSelectedSet}
								onDelSet={setInd => handleSetChange({ setInd }, formattedSets, mutate)} />
						</ContentBlock>
						{(selectedSet || selectedSet === 0) &&
							<SetDetails
								// key={get(selectedSet, ['category', 'id'], Math.random())}
								key={selectedSet}
								onChangeSetData={(vals, wasSubmitted) => {
									if(wasSubmitted) {
										setSelectedSet(null)
									}
									handleSetChange(vals, formattedSets, mutate)
								}}
								setData={getSetData()}
								onSelectCategory={value => existingCheck(value, formattedSets.map(({ category }) => category))}
							/>
						}
					</Fragment>
				)}
			</Mutation>
		</Fragment>
	)
}

export default withTranslation()(Selection)
